import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../utils/cn';

export type SelectMenuOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

type SelectMenuProps = {
  value: string;
  options: SelectMenuOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  buttonAriaLabel?: string;
};

export function SelectMenu({
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  className,
  triggerClassName,
  menuClassName,
  buttonAriaLabel,
}: SelectMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = useMemo(() => options.find((option) => option.value === value) ?? null, [options, value]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={buttonAriaLabel}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 pr-12 text-left text-sm shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          open ? 'border-slate-400 ring-2 ring-slate-100' : 'hover:border-slate-400 hover:bg-slate-50/60',
          triggerClassName,
        )}
      >
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate font-medium', selected ? 'text-slate-900' : 'text-slate-400')}>
            {selected?.label ?? placeholder}
          </span>
          {selected?.description ? <span className="mt-0.5 block truncate text-xs text-slate-500">{selected.description}</span> : null}
        </span>
        <ChevronDown
          size={18}
          className={cn('pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-transform', open ? 'rotate-180 text-slate-600' : '')}
        />
      </button>

      {open ? (
        <div
          role="listbox"
          className={cn(
            'absolute left-0 right-0 top-full z-40 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ring-slate-100',
            menuClassName,
          )}
        >
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) {
                    return;
                  }
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0',
                  option.disabled ? 'cursor-not-allowed bg-slate-50 text-slate-300' : active ? 'bg-blue-50 text-slate-950' : 'hover:bg-slate-50',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-sm', active ? 'font-semibold text-slate-950' : 'font-medium text-slate-800')}>
                    {option.label}
                  </span>
                  {option.description ? <span className="mt-0.5 block truncate text-xs text-slate-500">{option.description}</span> : null}
                </span>
                {active ? <Check size={16} className="mt-0.5 shrink-0 text-blue-600" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
